import React, { ReactElement, useRef } from 'react';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { ColorFunction } from '@styles/colors/types';
import ColorWrapper from '@styles/colors';
import TouchableElement from '@components/common/touchableElement';
import { useHover } from 'react-native-web-hooks';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { scale as initialScale } from '@components/frame';
import { isWeb } from '@helpers';
import { SimpleOnPress } from '@interfaces/base';

interface GlassButtonProps extends Partial<SimpleOnPress> {
  style?: StyleProp<ViewStyle>;
  C: ColorFunction;
  children: (params: { hovered: boolean }) => ReactElement;
  withoutHoverEffect?: boolean;
  scale?: ScaleFunction;
}

const GlassButtonComponent: React.FC<GlassButtonProps> = ({ children, onPress, style, C, withoutHoverEffect, scale = initialScale }) => {
  const buttonRef = useRef<View | null>(null);
  const hovered = useHover(buttonRef);

  const isHovered = withoutHoverEffect ? false : hovered;

  const commonStyles: StyleProp<ViewStyle> = {
    zIndex: 100000000,
    position: 'absolute',
    padding: 20,
    top: 0,
    left: 0,
    borderRadius: scale(SCREEN_BORDER_RADIUS),
    borderColor: isHovered ? C('Gray/Light/5') : C(`Hairline/Light/No trans [mobile]`, false),
    borderWidth: StyleSheet.hairlineWidth,
    ...(isWeb ? { outline: 'none' } : {}),
  };

  const content = (wrapperStyles?: StyleProp<ViewStyle>) => (
    <View ref={buttonRef} style={[wrapperStyles, commonStyles, style, { backgroundColor: isHovered ? C('Label/Light/Inverted') : 'rgba(240, 240, 240, 0.63)' }]}>
      {children({
        hovered: isHovered,
      })}
    </View>
  );

  if (onPress) {
    return <TouchableElement onPress={onPress}>{({boxStyle}) => content(boxStyle)}</TouchableElement>;
  }

  return content();
};

const GlassButton = ColorWrapper(GlassButtonComponent);

export { GlassButton };
